import * as React from 'react';
import {v4 as uuid_v4} from 'uuid';
import {CaptchaTypes} from '@discordapp/http-utils/V8APIError';

import {CaptchaAnalyticsEventProps, CaptchaEventName} from '../CaptchaTypes';
import HCaptcha from './types/HCaptcha';
import Recaptcha from './types/Recaptcha';
import RecaptchaEnterprise from './types/RecaptchaEnterprise';

import {RECAPTCHA_SITE_KEY} from '@discordapp/common/Constants';

interface CaptchaProps {
  theme: 'light' | 'dark';
  className?: string;
  size?: 'compact' | 'normal' | 'invisible';
  badge?: 'bottomright' | 'bottomleft' | 'inline';
  tabindex?: number;
  sitekey?: string;
  captchaService?: CaptchaTypes;
  rqdata?: string;
  userflow?: string;
  onRender?: () => void;
  onVerify: (responseToken: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  onChalExpired?: () => void;
  renderSpinner: () => React.ReactNode;
  track: (data: CaptchaAnalyticsEventProps) => void;
}

const Captcha = ({
  captchaService = CaptchaTypes.RECAPTCHA,
  sitekey,
  rqdata,
  size,
  userflow,
  onRender,
  onVerify,
  onError,
  onOpen,
  onClose,
  onChalExpired,
  track,
  ...props
}: CaptchaProps) => {
  const analyticsId = uuid_v4();
  if (sitekey == null || sitekey === '') {
    sitekey = RECAPTCHA_SITE_KEY;
  }

  const captchaRef = React.useRef<HCaptcha>(null);

  const [captchaInitialized, setCaptchaInitialized] = React.useState(false);
  const trackCaptchaEvent = React.useCallback(
    (eventName: CaptchaEventName) => {
      track({
        captcha_event_name: eventName,
        captcha_service: captchaService,
        sitekey,
        captcha_flow_key: analyticsId,
      });
    },
    [analyticsId, captchaService, sitekey, track],
  );

  const setCaptchaData = React.useCallback(() => {
    if (captchaService === CaptchaTypes.HCAPTCHA) {
      if (rqdata != null && rqdata !== '' && captchaRef.current != null) {
        captchaRef.current?.setData({rqdata});
      }

      if (size === 'invisible' && captchaRef.current != null) {
        captchaRef.current?.execute();
      }
    }
  }, [rqdata, captchaRef, size, captchaService]);

  const handleLoad = React.useCallback(() => {
    if (!captchaInitialized) {
      trackCaptchaEvent('initial-load');
      setCaptchaInitialized(true);
    }
    setCaptchaData();
  }, [captchaInitialized, trackCaptchaEvent, setCaptchaData]);

  React.useEffect(() => {
    setCaptchaData();
  }, [setCaptchaData]);

  // We don't really need this but HCaptcha onLoad is broken right now
  // and we want to emit a metric on initialization. Remove once HCaptcha
  // fixes their onLoad bug
  React.useEffect(() => {
    handleLoad();
  }, [handleLoad]);

  const handleError = React.useCallback(() => {
    trackCaptchaEvent('error');
    setCaptchaData();
    onError?.();
  }, [trackCaptchaEvent, setCaptchaData, onError]);

  const handleVerify = React.useCallback(
    (responseToken: string) => {
      trackCaptchaEvent('verify');
      onVerify(responseToken);
    },
    [onVerify, trackCaptchaEvent],
  );

  const handleRender = React.useCallback(() => {
    trackCaptchaEvent('render');
    onRender?.();
  }, [onRender, trackCaptchaEvent]);

  const handleOpen = React.useCallback(() => {
    trackCaptchaEvent('open');
    onOpen?.();
  }, [onOpen, trackCaptchaEvent]);

  const handleClose = React.useCallback(() => {
    trackCaptchaEvent('close');
    onClose?.();
    // We're resetting the captcha data here to deal with a very specific hcaptcha issue:
    // if you click the checkbox, then close the puzzle, then click the check and attempt to solve the puzzle
    // the rqdata will not be set correctly unless this call happens in between checkbox clicks.
    setCaptchaData();
  }, [onClose, trackCaptchaEvent, setCaptchaData]);

  const handleChalExpired = React.useCallback(() => {
    trackCaptchaEvent('chal-expire');
    onChalExpired?.();
  }, [onChalExpired, trackCaptchaEvent]);

  if (captchaService === CaptchaTypes.RECAPTCHA) {
    return (
      <Recaptcha
        {...props}
        onLoad={handleLoad}
        onRender={handleRender}
        onVerify={handleVerify}
        onError={handleError}
        sitekey={sitekey}
      />
    );
  }
  if (captchaService === CaptchaTypes.RECAPTCHA_ENTERPRISE) {
    return (
      <RecaptchaEnterprise
        {...props}
        onLoad={handleLoad}
        onRender={handleRender}
        onVerify={handleVerify}
        onError={handleError}
        sitekey={sitekey}
        action={userflow}
      />
    );
  }
  if (captchaService === CaptchaTypes.HCAPTCHA) {
    if (process.env.NODE_ENV === 'development') {
      return (
        <HCaptcha
          ref={captchaRef}
          sitekey={sitekey}
          // ignore cors issues on dev with the `host` prop
          // @ts-expect-error
          host="127.0.0.1"
          {...props}
          onLoad={handleLoad}
          onError={handleError}
          onVerify={handleVerify}
          onChalExpired={handleChalExpired}
          onOpen={handleOpen}
          onClose={handleClose}
          size={size}
          reCaptchaCompat={false}
        />
      );
    } else {
      return (
        <HCaptcha
          ref={captchaRef}
          {...props}
          sitekey={sitekey}
          onLoad={handleLoad}
          onError={handleError}
          onVerify={handleVerify}
          onChalExpired={handleChalExpired}
          onOpen={handleOpen}
          onClose={handleClose}
          size={size}
          reCaptchaCompat={false}
        />
      );
    }
  }
  // Use Recaptcha by default if something messes up
  return (
    <Recaptcha
      {...props}
      sitekey={sitekey}
      onLoad={handleLoad}
      onRender={handleRender}
      onVerify={handleVerify}
      onError={handleError}
    />
  );
};

export default Captcha;
